import { useEffect, useState } from 'react'
import clsx from 'clsx'

interface NumberStepperProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  className?: string
  label?: string
}

/**
 * Поле для целого числа с кнопками «−» / «+».
 * Поле можно очистить и ввести число с нуля; некорректное значение
 * (пусто, меньше min) приводится к допустимому при уходе с поля.
 */
export default function NumberStepper({
  value,
  onChange,
  min = 1,
  max,
  className,
  label,
}: NumberStepperProps) {
  // Черновик ввода: строка, чтобы поле можно было временно оставить пустым
  const [draft, setDraft] = useState(String(value))

  useEffect(() => {
    setDraft(String(value))
  }, [value])

  const clamp = (n: number) => Math.max(min, max !== undefined ? Math.min(max, n) : n)

  const handleInput = (raw: string) => {
    const digits = raw.replace(/\D/g, '')
    setDraft(digits)
    const n = parseInt(digits, 10)
    if (!Number.isNaN(n) && n >= min && (max === undefined || n <= max)) {
      onChange(n)
    }
  }

  const handleBlur = () => {
    const n = parseInt(draft, 10)
    const next = Number.isNaN(n) ? min : clamp(n)
    setDraft(String(next))
    if (next !== value) onChange(next)
  }

  const step = (delta: number) => onChange(clamp(value + delta))

  const buttonClass =
    'w-11 shrink-0 rounded-lg border border-gray-300 bg-white text-black text-xl leading-none ' +
    'active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100'

  return (
    <div className={clsx('flex items-stretch gap-2', className)}>
      <button
        type="button"
        onClick={() => step(-1)}
        disabled={value <= min}
        aria-label="Уменьшить"
        className={buttonClass}
      >
        −
      </button>
      <input
        aria-label={label}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={draft}
        onChange={(e) => handleInput(e.target.value)}
        onBlur={handleBlur}
        onFocus={(e) => e.target.select()}
        className="w-full min-w-0 p-2 rounded-lg border border-gray-300 bg-white text-black text-center"
      />
      <button
        type="button"
        onClick={() => step(1)}
        disabled={max !== undefined && value >= max}
        aria-label="Увеличить"
        className={buttonClass}
      >
        +
      </button>
    </div>
  )
}
